import React, { useState, useEffect, useRef } from 'react';

const SearchableDropdown = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Search and select...",
  disabled = false,
  required = false,
  displayKey = 'fullName',
  valueKey = 'id',
  emptyMessage = "No options available",
  searchPlaceholder = "Type to search...",
  noResultsMessage,
  secondaryKey = 'email',
  searchKeys,
  getOptionLabel,
  getOptionSecondary
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);

  // Update filtered options when search term or options change
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => {
        const normalizedSearchTerm = searchTerm.toLowerCase();
        const keysToSearch = searchKeys || [displayKey, secondaryKey];

        return keysToSearch.some((key) => {
          const valueToSearch = option[key];
          return String(valueToSearch || '').toLowerCase().includes(normalizedSearchTerm);
        });
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, displayKey, secondaryKey, searchKeys]);

  const getPrimaryLabel = (option) => {
    if (!option) return '';
    return getOptionLabel ? getOptionLabel(option) : (option[displayKey] || '');
  };

  const getSecondaryLabel = (option) => {
    if (!option) return '';
    return getOptionSecondary ? getOptionSecondary(option) : (option[secondaryKey] || '');
  };

  // Set display text based on selected value
  const getDisplayText = () => {
    if (!value) return '';
    const selectedOption = options.find(option => String(option[valueKey]) === String(value));
    return selectedOption ? getPrimaryLabel(selectedOption) : '';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setSearchTerm('');
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleOptionSelect = (option) => {
    onChange(option[valueKey]);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <div
        className="form-input"
        onClick={handleInputClick}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: disabled ? 'var(--gray-light)' : 'var(--white)',
          opacity: disabled ? 0.6 : 1,
          borderColor: required && !value ? 'var(--error)' : 'var(--gray-medium)'
        }}
      >
        <span style={{ 
          color: getDisplayText() ? 'var(--gray-dark)' : 'var(--gray-medium)',
          flex: 1,
          textAlign: 'left'
        }}
        data-i18n-skip="true"
        >
          {getDisplayText() || placeholder}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          {value && (
            <button
              type="button"
              onClick={handleClear}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--gray-dark)',
                padding: '2px',
                fontSize: '12px'
              }}
              title="Clear selection"
            >
              ✕
            </button>
          )}
          <span style={{ 
            color: 'var(--gray-dark)',
            fontSize: '12px',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: 'var(--white)',
          border: '2px solid var(--primary-blue)',
          borderRadius: '6px',
          boxShadow: '0 4px 15px rgba(74, 144, 226, 0.2)',
          zIndex: 1000,
          maxHeight: '200px',
          overflowY: 'auto',
          marginTop: '2px'
        }}>
          {/* Search input */}
          <div style={{ padding: '8px', borderBottom: '1px solid var(--gray-medium)' }}>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              style={{
                width: '100%',
                padding: '6px 8px',
                border: '1px solid var(--gray-medium)',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              autoFocus
            />
          </div>

          {/* Options list */}
          <div>
            {filteredOptions.length === 0 ? (
              <div style={{
                padding: '12px',
                color: 'var(--gray-dark)',
                textAlign: 'center',
                fontSize: '14px'
              }}>
                {searchTerm ? (noResultsMessage || `No results found for "${searchTerm}"`) : emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option[valueKey]}
                  onClick={() => handleOptionSelect(option)}
                  data-i18n-skip="true"
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--gray-light)',
                    transition: 'background-color 0.2s ease',
                    fontSize: '14px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--pale-blue)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <div style={{ fontWeight: '500', color: 'var(--dark-blue)' }}>
                    {getPrimaryLabel(option)}
                  </div>
                  {getSecondaryLabel(option) && (
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                      {getSecondaryLabel(option)}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
